"use client";

import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import CircularProgress from "@mui/material/CircularProgress"; // Added for loading spinner
import Typography from "@mui/material/Typography"; // For loading text
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { createBustop, updateBustop, deleteBustop, deleteAllBustop } from "../../api/bustop";
import customFetch from "hooks/fetchWrapper";
import ConfirmationDialog from "hooks/ConfirmationDialog";
import Menu from "@mui/material/Menu";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import * as XLSX from "xlsx";
import Autocomplete from "@mui/material/Autocomplete";

const BustopTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true); // New: loading state
  const [routes, setRoutes] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [areas, setAreas] = useState([]);

  const [openDeleteAllDialog, setOpenDeleteAllDialog] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [canConfirm, setCanConfirm] = useState(false);
  const countdownRef = useRef(null);

  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedBustop, setSelectedBustop] = useState(null);

  const [formData, setFormData] = useState({
    sid: null,
    bustopname: "",
    rid: "",
    pickupcode: "",
    pickuptime: "00:00",
    dropofftime: "00:00",
    eid: "",
    area: "",
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [excelMenuAnchorEl, setExcelMenuAnchorEl] = useState(null);
  const fileInputRef = useRef(null);

  // Combined fetch function with loading control
  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [bustopsRes, emiratesRes, areasRes] = await Promise.all([
        customFetch("bustops"),
        customFetch("emirates"),
        customFetch("areas"),
      ]);

      // Bustops
      if (bustopsRes.status && Array.isArray(bustopsRes.data)) {
        setRows(bustopsRes.data);
      } else {
        setRows([]);
      }

      // Emirates
      setEmirates(emiratesRes.data || emiratesRes || []);

      // Areas
      if (areasRes.status && Array.isArray(areasRes.data)) {
        setAreas(areasRes.data);
      } else {
        setAreas([]);
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
      setRows([]);
      setEmirates([]);
      setAreas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (formData.eid) {
      fetchRoutesByEmirate(formData.eid);
    } else {
      setRoutes([]);
    }
  }, [formData.eid]);

  useEffect(() => {
    if (openDeleteAllDialog && countdown > 0) {
      countdownRef.current = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      setCanConfirm(true);
      clearTimeout(countdownRef.current);
    }

    return () => clearTimeout(countdownRef.current);
  }, [openDeleteAllDialog, countdown]);

  const fetchRoutesByEmirate = async (eid) => {
    try {
      const data = await customFetch(`routes/by-emirate/${eid}`);
      if (data.status && Array.isArray(data.data)) {
        setRoutes(data.data);
      } else {
        setRoutes([]);
      }
    } catch (error) {
      console.error("Error fetching routes:", error);
      setRoutes([]);
    }
  };

  // Refresh only bustops (used after add/edit/delete/import)
  const fetchBustops = async () => {
    setLoading(true);
    try {
      const data = await customFetch("bustops");
      if (data.status && Array.isArray(data.data)) {
        setRows(data.data);
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error("Error fetching bus stops:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setIsEditMode(false);
    setFormData({
      sid: null,
      bustopname: "",
      rid: "",
      pickupcode: "",
      pickuptime: "00:00",
      dropofftime: "00:00",
      eid: "",
      area: "",
    });
    setRoutes([]);
    setOpenModal(true);
  };

  const handleOpenEditModal = (bustop) => {
    console.log("🚀 ~ handleOpenEditModal ~ bustop:", bustop);

    setIsEditMode(true);
    setSelectedBustop(bustop);

    const emirateName =
      bustop.route?.areas?.[0]?.emirate || bustop.route?.areas?.[0]?.emirateName || "";

    const matchedEmirate = emirates.find((e) => e.name.toLowerCase() === emirateName.toLowerCase());
    const eid = matchedEmirate ? matchedEmirate.eid : "";

    setFormData({
      sid: bustop.sid,
      bustopname: bustop.bustopname || "",
      rid: bustop.route?.routeno || "",
      pickupcode: bustop.pickupcode || "",
      pickuptime: bustop.pickuptime || "00:00",
      dropofftime: bustop.dropofftime || "00:00",
      eid: eid,
      area: bustop.area?.areaname || "",
    });

    if (eid) {
      fetchRoutesByEmirate(eid);
    } else {
      setRoutes([]);
    }

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedBustop(null);
  };

  const handleSubmit = async () => {
    if (!formData.bustopname || !formData.rid || !formData.pickupcode || !formData.area) {
      alert("Please fill all required fields (including Area)");
      return;
    }

    const payload = [
      {
        ...(isEditMode && { sid: formData.sid }),
        bustopname: formData.bustopname,
        rid: Number(formData.rid),
        pickupcode: formData.pickupcode,
        pickuptime: formData.pickuptime,
        dropofftime: formData.dropofftime,
        area: formData.area,
      },
    ];

    try {
      const response = isEditMode ? await updateBustop(payload) : await createBustop(payload);

      if (response.status) {
        fetchBustops(); // This will show loading again
        handleCloseModal();
        alert(isEditMode ? "Bus stop updated!" : "Bus stop added!");
      }
    } catch (error) {
      console.error("Operation failed:", error);
      alert("Operation failed");
    }
  };

  const handleCloseDeleteAll = () => {
    setOpenDeleteAllDialog(false);
    setCountdown(5);
    setCanConfirm(false);
    clearTimeout(countdownRef.current);
  };

  const handleConfirmDeleteAll = async () => {
    try {
      setLoading(true);
      await deleteAllBustop();
      await fetchBustops(); // refresh list
      handleCloseDeleteAll();
    } catch (error) {
      console.error("Delete all failed:", error);
      alert("Failed to delete all bus stops");
    } finally {
      handleCloseDeleteAll();
    }
  };

  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const headers = [["bustopname", "pickupcode", "pickuptime", "dropofftime", "route_id", "area"]];
    const exampleRow = [["BLDG 13,16", "33-01", "6:45", "14:35", 33, "GARDENS"]];
    const ws = XLSX.utils.aoa_to_sheet(headers);
    XLSX.utils.sheet_add_aoa(ws, exampleRow, { origin: -1 });

    ws["C2"] = { t: "s", v: "6:45" };
    ws["D2"] = { t: "s", v: "14:35" };

    XLSX.utils.book_append_sheet(wb, ws, "BusStops");
    XLSX.writeFile(wb, `bustops_template_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array", raw: false });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { raw: false, defval: "" });

        const formatTime = (val) => {
          if (!val) return "00:00";
          let str = String(val).trim();

          if (/^\d{1,2}:\d{2}$/.test(str)) return str.padStart(5, "0");

          if (/^\d{1,2}:\d{2}:\d{2} (AM|PM)$/i.test(str)) {
            const [time, period] = str.split(" ");
            let [h, m] = time.split(":").map(Number);
            if (period.toUpperCase() === "PM" && h !== 12) h += 12;
            if (period.toUpperCase() === "AM" && h === 12) h = 0;
            return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
          }

          if (!isNaN(str) && str !== "") {
            const num = parseFloat(str);
            if (num >= 0 && num < 1) {
              const formatted = XLSX.SSF.format("HH:mm", num);
              return formatted;
            }
          }

          return "00:00";
        };

        const payload = json
          .map((row) => ({
            bustopname: String(row.bustopname || "").trim(),
            pickupcode: String(row.pickupcode || "").trim(),
            pickuptime: formatTime(row.pickuptime),
            dropofftime: formatTime(row.dropofftime),
            rid: Number(row.route_id || 0),
            area: String(row.area || "").trim(),
          }))
          .filter((r) => r.bustopname && r.pickupcode && r.rid && r.area);

        if (payload.length === 0) {
          alert("No valid data found in the Excel file");
          return;
        }

        const res = await createBustop(payload);
        if (res.status) {
          fetchBustops(); // Triggers loading state
          alert(`Imported ${payload.length} bus stops!`);
        } else {
          alert("Import failed: " + (res.message || "Unknown error"));
        }
      } catch (err) {
        console.error(err);
        alert("Invalid Excel file or format");
      }
      e.target.value = null;
    };
    reader.readAsArrayBuffer(file);
  };

  const columns = [
    { field: "bustopname", headerName: "Bus Stop Name", flex: 1, minWidth: 180 },
    { field: "pickupcode", headerName: "Pickup Code", width: 120 },
    { field: "pickuptime", headerName: "Pick up Time", width: 120 },
    { field: "dropofftime", headerName: "Drop off Time", width: 120 },
    {
      field: "routename",
      headerName: "Route",
      width: 250,
      valueGetter: (p) => (p.row.route ? `${p.row.route.routeno} - ${p.row.route.routename}` : "—"),
    },
    {
      field: "area",
      headerName: "Area",
      width: 180,
      valueGetter: (p) => (p.row.area ? `${p.row.area.code} - ${p.row.area.areaname}` : "—"),
    },
    {
      field: "emirateName",
      headerName: "Emirate",
      width: 150,
      valueGetter: (p) => {
        const names = p.row.route?.areas?.map((a) => a.emirate || a.emirateName) || [];
        return names.length > 0 ? [...new Set(names)].join(", ") : "—";
      },
    },
    {
      field: "actions",
      type: "actions",
      width: 100,
      getActions: (p) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleOpenEditModal(p.row)}
        />,
        <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={handleDelete(p.id)} />,
      ],
    },
  ];

  const handleDelete = (id) => () => {
    setDeleteId(id);
    setOpenConfirmDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteBustop(deleteId);
      setRows((prev) => prev.filter((r) => r.sid !== deleteId));
    } catch (error) {
      console.error("Delete failed:", error);
    }
    setOpenConfirmDialog(false);
  };

  return (
    <Box sx={{ height: 700, width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 2 }}>
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            setOpenDeleteAllDialog(true);
            setCountdown(5);
            setCanConfirm(false);
          }}
        >
          Delete All Bustop
        </Button>
        <Button
          variant="contained"
          startIcon={<BackupTableIcon />}
          onClick={(e) => setExcelMenuAnchorEl(e.currentTarget)}
        >
          Excel
        </Button>
        <Menu
          anchorEl={excelMenuAnchorEl}
          open={Boolean(excelMenuAnchorEl)}
          onClose={() => setExcelMenuAnchorEl(null)}
        >
          <MenuItem onClick={handleDownloadTemplate}>
            <ListItemIcon>
              <FileDownloadIcon />
            </ListItemIcon>
            <ListItemText>Download Template</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => fileInputRef.current?.click()}>
            <ListItemIcon>
              <FileUploadIcon />
            </ListItemIcon>
            <ListItemText>Upload Excel</ListItemText>
          </MenuItem>
        </Menu>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".xlsx,.xls"
          style={{ display: "none" }}
        />
        <Button variant="contained" onClick={handleOpenAddModal}>
          Add New Bus Stop
        </Button>
      </Box>

      {/* DataGrid with loading overlay */}
      <Box sx={{ height: "100%", position: "relative" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.sid}
          pageSizeOptions={[10, 25, 50]}
          loading={loading} // Built-in loading prop (shows overlay)
          slots={{
            noRowsOverlay: () => (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Typography variant="body1">No bus stops found</Typography>
              </Box>
            ),
          }}
        />

        {/* Custom loading UI (optional, but nicer) */}
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
            }}
          >
            <CircularProgress size={40} />
            <Typography sx={{ mt: 2 }}>Loading bus stops...</Typography>
          </Box>
        )}
      </Box>

      {/* Modal and Confirmation Dialog remain unchanged */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            width: { xs: "90%", sm: 600 },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <h2>{isEditMode ? "Edit Bus Stop" : "Add New Bus Stop"}</h2>

          <TextField
            label="Bus Stop Name"
            fullWidth
            margin="dense"
            value={formData.bustopname}
            onChange={(e) => setFormData({ ...formData, bustopname: e.target.value })}
          />
          <TextField
            label="Pickup Code"
            fullWidth
            margin="dense"
            value={formData.pickupcode}
            onChange={(e) => setFormData({ ...formData, pickupcode: e.target.value })}
          />

          <TextField
            label="Pickup Time"
            type="time"
            fullWidth
            margin="dense"
            value={formData.pickuptime}
            onChange={(e) => setFormData({ ...formData, pickuptime: e.target.value })}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 60 }}
          />
          <TextField
            label="Drop-off Time"
            type="time"
            fullWidth
            margin="dense"
            value={formData.dropofftime}
            onChange={(e) => setFormData({ ...formData, dropofftime: e.target.value })}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 60 }}
          />

          <FormControl fullWidth margin="dense">
            <InputLabel>Emirate</InputLabel>
            <Select
              value={formData.eid}
              onChange={(e) => setFormData({ ...formData, eid: e.target.value, rid: "" })}
            >
              {emirates?.map((e) => (
                <MenuItem key={e.eid} value={e.eid}>
                  {e.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            options={routes}
            getOptionLabel={(option) => `${option.routeno} - ${option.routename}`}
            value={routes.find((r) => r.routeno === formData.rid) || null}
            onChange={(event, newValue) => {
              setFormData({ ...formData, rid: newValue ? newValue.routeno : "" });
            }}
            disabled={!formData.eid}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Route"
                margin="dense"
                fullWidth
                placeholder="Type to search route..."
              />
            )}
            noOptionsText="No routes found"
            sx={{ mt: 1 }}
          />

          <Autocomplete
            options={areas}
            getOptionLabel={(option) => `${option.code} - ${option.areaname}`}
            value={areas.find((a) => a.areaname === formData.area) || null}
            onChange={(event, newValue) => {
              setFormData({ ...formData, area: newValue ? newValue.areaname : "" });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Area"
                margin="dense"
                fullWidth
                placeholder="Type to search area..."
              />
            )}
            noOptionsText="No areas available"
            sx={{ mt: 1 }}
          />

          <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {isEditMode ? "Update" : "Create"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={openDeleteAllDialog} onClose={handleCloseDeleteAll}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Delete ALL Bus Stops?
          </Typography>
          <Typography variant="body1" sx={{ my: 3 }}>
            This action cannot be undone.
          </Typography>
          <Typography variant="h4" sx={{ my: 4, fontWeight: "bold" }}>
            {countdown}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {canConfirm
              ? "You can now confirm deletion"
              : "Please wait for the countdown to finish"}
          </Typography>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "center", gap: 2 }}>
            <Button onClick={handleCloseDeleteAll} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDeleteAll}
              disabled={!canConfirm}
            >
              Yes, Delete All
            </Button>
          </Box>
        </Box>
      </Modal>

      <ConfirmationDialog
        open={openConfirmDialog}
        title="Delete Bus Stop"
        description="Are you sure?"
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default BustopTab;
